import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';

const MLPTeamSetup = ({ onTeamSetup, onBack }) => {
  const [teams, setTeams] = useState({
    A: { name: 'Team A', f1: '', f2: '', m1: '', m2: '' },
    B: { name: 'Team B', f1: '', f2: '', m1: '', m2: '' },
    C: { name: 'Team C', f1: '', f2: '', m1: '', m2: '' },
    D: { name: 'Team D', f1: '', f2: '', m1: '', m2: '' }
  });

  // Test data for quick setup
  const testPlayers = {
    A: {
      name: 'Thunder',
      f1: 'Sarah Johnson (3.8)',
      f2: 'Emma Davis (3.6)',
      m1: 'Mike Wilson (3.9)',
      m2: 'James Brown (3.7)'
    },
    B: {
      name: 'Lightning',
      f1: 'Ashley Miller (3.7)',
      f2: 'Jessica Garcia (3.8)',
      m1: 'David Martinez (3.6)',
      m2: 'Chris Anderson (4.0)'
    },
    C: {
      name: 'Storm',
      f1: 'Lauren Thompson (3.9)',
      f2: 'Megan White (3.5)',
      m1: 'Ryan Taylor (3.8)',
      m2: 'Alex Rodriguez (3.7)'
    },
    D: {
      name: 'Cyclone',
      f1: 'Nicole Lee (3.6)',
      f2: 'Samantha Moore (3.9)',
      m1: 'Kevin Jackson (3.5)',
      m2: 'Tyler Harris (3.8)'
    }
  };

  const handleInputChange = (team, field, value) => {
    setTeams(prev => ({
      ...prev,
      [team]: {
        ...prev[team],
        [field]: value
      }
    }));
  };

  const loadTestData = () => {
    setTeams(testPlayers);
  };

  const clearAllData = () => {
    setTeams({
      A: { name: 'Team A', f1: '', f2: '', m1: '', m2: '' },
      B: { name: 'Team B', f1: '', f2: '', m1: '', m2: '' },
      C: { name: 'Team C', f1: '', f2: '', m1: '', m2: '' },
      D: { name: 'Team D', f1: '', f2: '', m1: '', m2: '' }
    });
  };

  const validateTeams = () => {
    for (let team of Object.values(teams)) {
      if (!team.name.trim() || !team.f1.trim() || !team.f2.trim() || 
          !team.m1.trim() || !team.m2.trim()) {
        return false;
      }
    }
    return true;
  };

  const handleBeginMatches = () => {
    if (!validateTeams()) {
      alert('Please fill in all team names and player names before beginning matches.');
      return;
    }
    onTeamSetup(teams);
  };

  return (
    <div className="player-setup">
      {/* Header with Back Button */}
      <div className="setup-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
          <button 
            onClick={onBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            <ArrowLeft size={16} />
            Back to Setup
          </button>
          <h2 style={{ margin: 0 }}>MLP Tournament Setup</h2>
        </div>
        <p>Enter team names and player information for all 4 teams (2 males, 2 females per team)</p>
      </div>
      
      {/* Test Data Controls */}
      <div className="import-section">
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginBottom: '24px' }}>
          <button 
            onClick={loadTestData}
            className="import-button primary"
          >
            Load Test Data
          </button>
          <button 
            onClick={clearAllData}
            className="import-button secondary"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Teams Grid */}
      <div className="import-section">
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'auto repeat(4, 1fr)',
          gap: '16px',
          alignItems: 'center'
        }}>
          {/* Header Row */}
          <div></div>
          {['A', 'B', 'C', 'D'].map(team => (
            <div key={team} style={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#deff01',
              fontSize: '18px',
              padding: '12px'
            }}>
              Team {team}
            </div>
          ))}

          {/* Team Name Row */}
          <div style={{ fontWeight: 'bold', color: 'white', padding: '12px' }}>
            Team Name:
          </div>
          {['A', 'B', 'C', 'D'].map(team => (
            <input
              key={team}
              type="text"
              value={teams[team].name}
              onChange={(e) => handleInputChange(team, 'name', e.target.value)}
              placeholder={`Team ${team}`}
              style={{
                padding: '12px',
                borderRadius: '8px',
                border: 'none',
                background: 'rgba(255,255,255,0.1)',
                color: 'white',
                fontSize: '14px'
              }}
            />
          ))}

          {/* Female 1 Row */}
          <div style={{ fontWeight: 'bold', color: 'white', padding: '12px' }}>
            Female 1:
          </div>
          {['A', 'B', 'C', 'D'].map(team => (
            <input
              key={team}
              type="text"
              value={teams[team].f1}
              onChange={(e) => handleInputChange(team, 'f1', e.target.value)}
              placeholder="Name (Rating)"
              style={{
                padding: '12px',
                borderRadius: '8px',
                border: 'none',
                background: 'rgba(255,255,255,0.1)',
                color: 'white',
                fontSize: '14px'
              }}
            />
          ))}

          {/* Female 2 Row */}
          <div style={{ fontWeight: 'bold', color: 'white', padding: '12px' }}>
            Female 2:
          </div>
          {['A', 'B', 'C', 'D'].map(team => (
            <input
              key={team}
              type="text"
              value={teams[team].f2}
              onChange={(e) => handleInputChange(team, 'f2', e.target.value)}
              placeholder="Name (Rating)"
              style={{
                padding: '12px',
                borderRadius: '8px',
                border: 'none',
                background: 'rgba(255,255,255,0.1)',
                color: 'white',
                fontSize: '14px'
              }}
            />
          ))}

          {/* Male 1 Row */}
          <div style={{ fontWeight: 'bold', color: 'white', padding: '12px' }}>
            Male 1:
          </div>
          {['A', 'B', 'C', 'D'].map(team => (
            <input
              key={team}
              type="text"
              value={teams[team].m1}
              onChange={(e) => handleInputChange(team, 'm1', e.target.value)}
              placeholder="Name (Rating)"
              style={{
                padding: '12px',
                borderRadius: '8px',
                border: 'none',
                background: 'rgba(255,255,255,0.1)',
                color: 'white',
                fontSize: '14px'
              }}
            />
          ))}

          {/* Male 2 Row */}
          <div style={{ fontWeight: 'bold', color: 'white', padding: '12px' }}>
            Male 2:
          </div>
          {['A', 'B', 'C', 'D'].map(team => (
            <input
              key={team}
              type="text"
              value={teams[team].m2}
              onChange={(e) => handleInputChange(team, 'm2', e.target.value)}
              placeholder="Name (Rating)"
              style={{
                padding: '12px',
                borderRadius: '8px',
                border: 'none',
                background: 'rgba(255,255,255,0.1)',
                color: 'white',
                fontSize: '14px'
              }}
            />
          ))}
        </div>
      </div>

      {/* Begin Tournament Button */}
      <div className="start-session">
        <button 
          onClick={handleBeginMatches}
          className="start-button"
          disabled={!validateTeams()}
          style={{
            opacity: validateTeams() ? 1 : 0.5,
            cursor: validateTeams() ? 'pointer' : 'not-allowed'
          }}
        >
          Begin Tournament
        </button>
        
        {!validateTeams() && (
          <p className="min-players-note">
            Please fill in all team names and player names before beginning matches.
          </p>
        )}
      </div>
    </div>
  );
};

export default MLPTeamSetup;